import { LiveTopic } from '../live-topics-types';
import type { SvgIconComponent } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import { Box } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { EntityChips, getEntityChipIconSx, type EntityChipsSize } from './EntityChips';

export type EntitiesDisplayMode = 'long' | 'short';

type EntityTypeKey = 'people' | 'orgs' | 'places';

/** Section label messages for long mode; ids and defaultMessage are static for formatjs extractor. */
const ENTITY_LABEL_MESSAGES: Record<EntityTypeKey, ReturnType<typeof defineMessage>> = {
  people: defineMessage({
    id: 'logger.streamScreen.liveTopics.entities.people',
    defaultMessage: 'Personas',
  }),
  orgs: defineMessage({
    id: 'logger.streamScreen.liveTopics.entities.orgs',
    defaultMessage: 'Organizaciones',
  }),
  places: defineMessage({
    id: 'logger.streamScreen.liveTopics.entities.places',
    defaultMessage: 'Lugares',
  }),
};

/** Config for each entity type: icon and array key on entities. */
const ENTITY_TYPE_CONFIG: ReadonlyArray<{
  key: EntityTypeKey;
  Icon: SvgIconComponent;
}> = [
  { key: 'people', Icon: PersonIcon },
  { key: 'orgs', Icon: BusinessIcon },
  { key: 'places', Icon: PlaceIcon },
];

/**
 * Renders topic entities (people, orgs, places) as chips.
 * Short mode: all types in one row with icon per chip; long mode: grouped by type with section titles.
 * Shows at most maxPerType per type; extra entities are shown as a "+N" chip with tooltip.
 */
export function TopicEntities({
  entities,
  displayMode,
  size = 'small',
  maxPerType = 2,
}: {
  entities: NonNullable<LiveTopic['entities']>;
  displayMode: EntitiesDisplayMode;
  size?: EntityChipsSize;
  /** Maximum number of entity chips to show per type before "+N". Defaults to 2. */
  maxPerType?: number;
}) {
  const { formatMessage } = useIntl();
  const isShort = displayMode === 'short';
  const iconSx = getEntityChipIconSx(size);
  const labelVariant = size === 'small' ? 'caption' : 'body2';

  if (isShort) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: size === 'small' ? 0.5 : 1 }}>
        {ENTITY_TYPE_CONFIG.map(({ key, Icon }) => {
          const names = entities[key];
          if (!Array.isArray(names)) {
            return null;
          }

          return (
            <EntityChips
              key={key}
              names={names}
              keyPrefix={key}
              Icon={Icon}
              showIconOnEachChip
              size={size}
              maxPerType={maxPerType}
            />
          );
        })}
      </Box>
    );
  }

  const sectionMarginBottom = { people: 0.75, orgs: 0.75, places: 0 } as const;

  return (
    <>
      {ENTITY_TYPE_CONFIG.map(({ key, Icon }) => {
        const names = entities[key];
        if (!Array.isArray(names) || names.length === 0) {
          return null;
        }

        return (
          <Box key={key} sx={{ mb: sectionMarginBottom[key] }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 0.25,
                color: 'text.secondary',
              }}
            >
              <Icon sx={iconSx} />
              <Box component="span" sx={{ typography: labelVariant }}>
                {formatMessage(ENTITY_LABEL_MESSAGES[key])}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: size === 'small' ? 0.5 : 1 }}>
              <EntityChips
                names={names}
                keyPrefix={key}
                Icon={Icon}
                showIconOnEachChip={false}
                size={size}
                maxPerType={maxPerType}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
}
